import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { PeriodsAgo, TeamMember } from "../../../../@types/custom";
import { getMyTeamData } from "../../AdminHome/api";
import { getCalls } from "../api/calls";
import { db } from "firebaseConfig";
import { useModal } from "mui-modal-provider";
import { ConfirmationDialog } from "components/__haim/Common/ConfirmationDialog";
import type { CallSummary } from "../../../../@types/sharedSchema";
import type { DateRangeFilter } from "utils/listings/table";

type CallsData = Record<string, CallSummary[]>;

export function useCallsData(
  selectedUsers: string[],
  selectedPeriod: PeriodsAgo | DateRangeFilter,
  fromPhoneNumber?: string
) {
  const [myTeamData, setMyTeamData] = useState<TeamMember[]>([]);
  const [callsData, setCallsData] = useState<CallsData>({});
  const [isLoadingCallsData, setIsLoadingCallsData] = useState(true);
  const { organization, userSettings, isLoadingUser } = useFirestore();

  const { user } = useAuth();

  const { t: translate } = useTranslation();

  const { showModal } = useModal();

  useEffect(() => {
    if (!organization || !organization.admins?.includes(user.email)) return;

    async function _getMyTeamData() {
      const _myTeamData = await getMyTeamData(organization.users, false);
      setMyTeamData(_myTeamData);
    }

    _getMyTeamData();
  }, [organization, user?.email]);

  useEffect(() => {
    async function _getCalls() {
      setIsLoadingCallsData(true);
      const _callsData = await getCalls(
        [...myTeamData, userSettings as TeamMember],
        selectedPeriod,
        fromPhoneNumber
      );
      setCallsData(_callsData);
      setIsLoadingCallsData(false);
    }

    if (!isLoadingUser) {
      _getCalls();
    }
  }, [fromPhoneNumber, selectedPeriod, myTeamData, userSettings, isLoadingUser]);

  const deleteCall = async (call: CallSummary): Promise<void> => {
    const {
      _metadata: { firestorePath }
    } = call;

    try {
      await db.doc(firestorePath).delete();

      const newCallsData: CallsData = {};

      for (const member in callsData) {
        newCallsData[member] = callsData[member].filter((c) => c.id !== call.id);
      }

      setCallsData(newCallsData);
    } catch (error) {
      console.error("Error deleting call:", error);

      const errorMessage =
        error instanceof Error ? error.message : translate("callsPage.error.unknownError");

      showModal(ConfirmationDialog, {
        title: translate("callsPage.error.deleteCall"),
        description: errorMessage
      });
    }
  };

  const calls: CallSummary[] = useMemo(() => {
    return Object.keys(callsData)
      .filter((member) => (selectedUsers.length ? selectedUsers.includes(member) : member))
      .map((member) => callsData[member])
      .flat()
      .sort((a, b) => (!a["startAt"] || a["startAt"] < b["startAt"] ? 1 : -1));
  }, [callsData, selectedUsers]);

  const members = useMemo(() => Object.keys(callsData), [callsData]);

  return { calls, members, myTeamData, isLoadingCallsData, deleteCall };
}
